import { FormInstance, UploadFile } from 'antd';
import { DossierDocumentsFormType } from 'components/DossierDocumentsForm';
import Colors from 'const/colors';
import { DossierMPRStatus, DossierStatus, ModifyDossier } from 'types/Dossier';
import { OperationStatus } from 'types/Operation';

const orange = Colors.GrandPublic;
const red = Colors.GestionnaireHabitat;
const green = Colors.Agriculture;
const gris = Colors.Industriels;

export const getDossierStatusColor = (
  status: DossierStatus | DossierMPRStatus | OperationStatus,
): string => {
  // Référence couleurs: https://www.figma.com/design/F2meC6lua7aaJkBZbouD0w/Plateforme-Partenaire-Logement?node-id=968-96&m=dev
  switch (status) {
    case DossierStatus.EtudeEnCours:
    case DossierStatus.OffreEmise:
    case DossierStatus.EnAttenteDeSignature:
    case DossierStatus.ChantierEnCours:
    case DossierStatus.PieceEnAttente:
    case DossierStatus.EnAttenteDeControle:
    case DossierStatus.EnCoursDeValidation:
      return orange;
    case DossierStatus.InformationsRequises:
    case DossierStatus.ChantierNonConforme:
    case DossierStatus.Incomplet:
      return red;

    case DossierStatus.PrimeVersee:
    case DossierStatus.Valide:
      return green;

    case DossierStatus.Refuse:
    case DossierStatus.Abandon:
    default:
      return gris;
  }
};

export const getDossierMprStatusColor = (
  status: DossierMPRStatus,
): string | undefined => {
  // Référence couleurs: https://www.figma.com/design/F2meC6lua7aaJkBZbouD0w/Plateforme-Partenaire-Logement?node-id=968-96&m=dev
  switch (status) {
    case DossierMPRStatus.EnCoursDeTraitement:
      return orange;

    case DossierMPRStatus.Refuse:
    case DossierMPRStatus.DossierNonConforme:
    case DossierMPRStatus.DemandeDeProrogationEnCours:
      return red;

    case DossierMPRStatus.Finalise:
    case DossierMPRStatus.PrimeAgreee:
    case DossierMPRStatus.DemandeDeSoldeDeposee:
    case DossierMPRStatus.PaiementANAHRecu:
    case DossierMPRStatus.PrimeMPRPayee:
      return green;

    case DossierMPRStatus.DossierRefuse:
    case DossierMPRStatus.PrimeArriveeAForclusion:
    case DossierMPRStatus.Abandon:
      return gris;

    default:
      return;
  }
};

export function appendDossierFormDataDocuments(
  files: UploadFile[],
  formData: FormData,
  prefix: string[],
) {
  files
    .filter(({ url }: UploadFile) => !url) // Ignorer les fichiers déjà uploadés
    .forEach((file: UploadFile) => {
      // Comme il n'est pas possible de donner des noms de champs dynamiques pour les fichiers,
      // on concatène le type et le sous-type de fichier au nom de fichier en utilisant des ***
      // (qui sont des caractères inutilisables en temps normal dans un nom de fichier)
      const newName = [...prefix, file.name].join(' *** ');
      // Créée un clone du fichier avec le nouveau nom
      const newFile = new File([file as unknown as File], newName, {
        type: file.type,
      });
      // Ajoute le fichier au données du formulaire
      formData.append('documents', newFile);
    });
}

export function getModifyDossierFormData(
  form: FormInstance<DossierDocumentsFormType>,
): FormData {
  const formData = new FormData();

  const data: ModifyDossier = {};
  const addData = <K extends keyof ModifyDossier>(
    fieldName: K,
    value: ModifyDossier[K],
  ) => {
    if (value !== null && value !== undefined) {
      data[fieldName] = value;
    }
  };
  type DateFormField =
    | 'dateVisiteTechnique'
    | 'dateEditionDevis'
    | 'dateSignatureDevis'
    | 'dateRAI'
    | 'dateEmissionFacture'
    | 'dateDebutTravauxAdmin';
  type ArrayFormField = 'deletedDocumentUIDs';
  const addDateData = (fieldName: DateFormField) =>
    addData(fieldName, form.getFieldValue(fieldName)?.format('YYYY-MM-DD'));
  const addArrayData = (fieldName: ArrayFormField) =>
    addData(fieldName, form.getFieldValue(fieldName) || []);

  const appendFiles = (files: UploadFile[], prefix: string[]) => {
    appendDossierFormDataDocuments(files, formData, prefix);
  };

  appendFiles(form.getFieldValue('documentsDebutTravaux'), [
    'documentsDebutTravaux',
  ]);
  addDateData('dateVisiteTechnique');
  addDateData('dateEditionDevis');
  const montantDevisValue = form.getFieldValue('montantDevis');
  if (montantDevisValue !== undefined) {
    data.montantDevis = montantDevisValue;
  }
  if (form.getFieldValue('devisSigne')) {
    addDateData('dateSignatureDevis');
  } else {
    addData('dateSignatureDevis', '');
  }

  appendFiles(form.getFieldValue('documentsRAI'), ['documentsRAI']);
  addDateData('dateRAI');

  appendFiles(form.getFieldValue('documentsFinTravaux'), [
    'documentsFinTravaux',
  ]);
  addDateData('dateEmissionFacture');
  addDateData('dateDebutTravauxAdmin');

  appendFiles(form.getFieldValue('documentsAttestationHonneurBeneficiaire'), [
    'documentsAttestationHonneurBeneficiaire',
  ]);

  appendFiles(form.getFieldValue('documentsTechnique'), ['documentsTechnique']);

  const documentsOperation = form.getFieldValue('documentsOperation') || [];
  Object.entries(documentsOperation).forEach(([category]) =>
    appendFiles(form.getFieldValue('documentsOperation')[category], [
      'documentsOperation',
      category,
    ]),
  );

  const documentsMPR = form.getFieldValue('documentsMPR') || [];
  Object.entries(documentsMPR).forEach(([category]) =>
    appendFiles(form.getFieldValue('documentsMPR')[category], [
      'documentsMPR',
      category,
    ]),
  );

  appendFiles(form.getFieldValue('documentsAutre'), ['documentsAutre']);

  // Fichiers à supprimer
  addArrayData('deletedDocumentUIDs');

  // Données du dossier au format JSON
  formData.append('data', JSON.stringify(data));

  return formData;
}
